import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Headphones, Search, Send, Loader2, CheckCheck, Phone, Mail, Building2, ArrowLeft, Lock, Unlock, MessageSquarePlus, X,
  Inbox, Clock, MessagesSquare, Gift, ShieldCheck, Facebook, MessageCircle, Zap, Paperclip, ChevronDown,
} from 'lucide-react';
import { API_URL } from '../../config';
import { AttachmentView } from '../client/SupportChat';

// ============================================================
// ADMIN - Support : conversations avec les clients (chat direct dans l'application).
// À gauche la liste (non lus en premier), à droite la conversation, avec l'offre du client sous les yeux.
// ============================================================

const api = (path, opts = {}) => fetch(`${API_URL}/api/admin${path}`, {
  ...opts, headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}`, 'Content-Type': 'application/json', ...(opts.headers || {}) },
}).then(r => r.json());

const hhmm = (iso) => new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
const dayLabel = (iso) => {
  const d = new Date(iso); const t = new Date(); const y = new Date(Date.now() - 86400000);
  if (d.toDateString() === t.toDateString()) return "Aujourd'hui";
  if (d.toDateString() === y.toDateString()) return 'Hier';
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
};
const ago = (iso) => {
  if (!iso) return '';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 90) return "à l'instant";
  if (s < 3600) return `${Math.round(s / 60)} min`;
  if (s < 86400) return `${Math.round(s / 3600)} h`;
  if (s < 86400 * 7) return `${Math.round(s / 86400)} j`;
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
};

const CANNED_MG = [
  { label: 'Fifandraisana voalohany', text: "Salama, ny ekipa TeckiA ity 👋 Hiantso anao izahay afaka kelikely mba hiresaka momba ny asanao sy hanamboatra ny agent-nao. Azo atao ve izao?" },
  { label: "Fampahalalana ho an'ny agent", text: "Mba hanamboatra ny agent-nao, azafady lazao anay: 1) inona no amidinao na atolotrao, 2) ny vidiny, 3) ny ora fiasanao sy ny faritra anaterana entana, 4) ahoana no fandoavan'ny mpanjifanao vola?" },
  { label: 'Agent mandeha', text: "Vaovao tsara: mandeha ny agent IA-nao ✅ Mamaly ny mpanjifanao izao. Aza misalasala manoratra anay raha mila ovaina na inona na inona." },
  { label: 'Voaray ny fandoavam-bola', text: "Voaray ny fandoavanao vola, misaotra! Mandeha ny offre-nao. Mila fanampiana ve ho amin'ny manaraka?" },
  { label: 'Fampahatsiahivana', text: "Salama, tapitra ny fotoana fanandramana maimaim-poana. Raha te hanohy ianao dia mandoa ny abonnement mivantana ao amin'ny application (MVola, Orange Money na carte). Eto foana aho raha manana fanontaniana ianao." },
];
const CANNED = [
  { label: "Prise de contact", text: "Bonjour, ici l'équipe TeckiA 👋 Nous allons vous appeler dans quelques instants pour discuter de votre activité et configurer votre agent. Est-ce un bon moment ?" },
  { label: "Infos pour l'agent", text: "Pour configurer votre agent, pouvez-vous nous indiquer : 1) ce que vous vendez ou proposez, 2) vos prix, 3) vos horaires et votre zone de livraison, 4) comment vos clients vous paient ?" },
  { label: "Agent activé", text: "Bonne nouvelle : votre agent IA est activé ✅ Il répond maintenant à vos clients. N'hésitez pas à nous écrire si vous voulez ajuster quelque chose." },
  { label: "Paiement reçu", text: "Nous avons bien reçu votre paiement, merci ! Votre offre est active. Besoin d'aide pour la suite ?" },
  { label: "Rappel de paiement", text: "Bonjour, votre essai gratuit est terminé. Pour continuer, vous pouvez payer l'abonnement directement dans l'application (MVola, Orange Money ou carte). Je reste disponible si vous avez une question." },
];

const KIND = { trial: 'Essai', paid: 'Payant', gift: 'Offert' };
const STAT = { active: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10', expired: 'text-amber-300 border-amber-500/40 bg-amber-500/10', stopped: 'text-rose-300 border-rose-500/40 bg-rose-500/10' };

function Kpi({ icon: Icon, label, value, tone, onClick, active }) {
  return (
    <button onClick={onClick} className={`text-left rounded-xl border px-3 py-2 transition ${active ? 'bg-white/15 border-white/40' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}>
      <div className="flex items-center gap-1.5 text-[11px] text-gray-400"><Icon className={`w-3.5 h-3.5 ${tone}`} /> {label}</div>
      <div className="text-xl font-bold leading-tight">{value}</div>
    </button>
  );
}

function NewConversation({ onClose, onStarted }) {
  const [q, setQ] = useState('');
  const [users, setUsers] = useState([]);
  const [user, setUser] = useState(null);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (user || q.trim().length < 2) { setUsers([]); return undefined; }
    const t = setTimeout(async () => { const r = await api(`/agent-offers/users?q=${encodeURIComponent(q.trim())}`); if (r.success) setUsers(r.users); }, 300);
    return () => clearTimeout(t);
  }, [q, user]);
  const go = async () => {
    if (!user || !body.trim()) { setErr('Choisissez un client et écrivez un message.'); return; }
    setBusy(true); setErr('');
    const r = await api('/support/start', { method: 'POST', body: JSON.stringify({ user_id: user.id, body }) });
    if (r.success) { onStarted(r.thread_id); onClose(); } else { setErr(r.message || 'Erreur'); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-slate-900 border border-white/15 shadow-2xl p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h3 className="font-bold">Écrire à un client</h3><button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10"><X className="w-4 h-4" /></button></div>
        {user ? (
          <div className="flex items-center justify-between gap-2 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm"><span className="truncate"><b>{user.name || user.email}</b> <span className="text-gray-400">{user.email}</span></span><button onClick={() => setUser(null)}><X className="w-4 h-4 text-gray-400" /></button></div>
        ) : (
          <div className="relative">
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Chercher un client (nom, email, téléphone)…" autoFocus className="w-full bg-black/30 border border-white/15 rounded-xl px-3 py-2 text-sm outline-none focus:border-violet-400" />
            {users.length > 0 && <div className="absolute z-10 left-0 right-0 mt-1 max-h-56 overflow-auto rounded-xl bg-slate-800 border border-white/15 shadow-xl">
              {users.map(u => <button key={u.id} onClick={() => { setUser(u); setUsers([]); }} className="w-full text-left px-3 py-2 text-sm hover:bg-white/10"><b>{u.name || u.email}</b><span className="block text-xs text-gray-400">{u.email}</span></button>)}
            </div>}
          </div>
        )}
        <textarea value={body} onChange={e => setBody(e.target.value)} rows={4} placeholder="Votre message…" className="w-full bg-black/30 border border-white/15 rounded-xl px-3 py-2 text-sm outline-none focus:border-violet-400 resize-none" />
        {err && <p className="text-sm text-rose-300">{err}</p>}
        <button onClick={go} disabled={busy} className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-violet-600 to-indigo-600 disabled:opacity-50 flex items-center justify-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Envoyer (le client est notifié)</button>
      </div>
    </div>
  );
}

const FLAG = { fr: '🇫🇷', mg: '🇲🇬' };
const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} Mo` : `${Math.max(1, Math.round(b / 1024))} Ko`);

function Conversation({ tid, onBack, onChanged }) {
  const [data, setData] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');
  const [canned, setCanned] = useState(false);
  const [atBottom, setAtBottom] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const lastRef = useRef('');
  const endRef = useRef(null);
  const stick = useRef(true);
  const boxRef = useRef(null);
  const changedRef = useRef(onChanged);
  changedRef.current = onChanged;

  const load = useCallback(async (first) => {
    const qs = !first && lastRef.current ? `?after=${encodeURIComponent(lastRef.current)}` : '';
    const r = await api(`/support/threads/${tid}${qs}`);
    if (!r.success) return;
    if (first) { setData(r); setMsgs(r.messages); lastRef.current = r.messages.length ? r.messages[r.messages.length - 1].created_at : ''; changedRef.current(); }
    else {
      setData(d => ({ ...d, thread: r.thread, user_seen_at: r.user_seen_at, client_online: r.client_online }));
      if (r.messages.length) {
        setMsgs(prev => { const ids = new Set(prev.map(m => m.id)); const add = r.messages.filter(m => !ids.has(m.id)); return add.length ? [...prev, ...add] : prev; });
        lastRef.current = r.messages[r.messages.length - 1].created_at;
        changedRef.current();
      }
    }
  }, [tid]);

  useEffect(() => {
    setData(null); setMsgs([]); lastRef.current = ''; stick.current = true;
    load(true);
    const t = setInterval(() => { if (!document.hidden) load(false); }, 3000);
    return () => clearInterval(t);
  }, [tid, load]);
  useEffect(() => { if (stick.current) endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [msgs]);

  const send = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true); setErr('');
    const r = await api(`/support/threads/${tid}/messages`, { method: 'POST', body: JSON.stringify({ body }) });
    setSending(false);
    if (r.success) { setText(''); stick.current = true; setMsgs(p => p.some(m => m.id === r.message.id) ? p : [...p, r.message]); lastRef.current = r.message.created_at; changedRef.current(); }
    else setErr(r.message || 'Message non envoyé');
  };
  const upload = async (file) => {
    if (!file || uploading) return;
    setUploading(true); setErr('');
    const fd = new FormData(); fd.append('file', file); fd.append('body', text.trim());
    try {
      const r = await fetch(`${API_URL}/api/admin/support/threads/${tid}/attachments`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }, body: fd });
      const d = await r.json();
      if (d.success) { setText(''); stick.current = true; setMsgs(p => p.some(m => m.id === d.message.id) ? p : [...p, d.message]); lastRef.current = d.message.created_at; changedRef.current(); }
      else setErr(d.message || 'Fichier non envoyé');
    } catch (_) { setErr('Connexion impossible'); } finally { setUploading(false); }
  };
  const setStatus = async (status) => { await api(`/support/threads/${tid}/status`, { method: 'POST', body: JSON.stringify({ status }) }); load(true); };

  if (!data) return <div className="flex-1 min-h-0 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  const th = data.thread;
  const offers = data.offers || { slots: [], pages: [] };
  let lastDay = '';
  return (
    <div className="flex-1 min-w-0 min-h-0 h-full flex flex-col relative">
      {/* en-tête client */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-white/10 bg-black/25">
        <div className="flex items-start gap-3">
          <button onClick={onBack} className="md:hidden p-2 rounded-xl bg-white/10 mt-0.5"><ArrowLeft className="w-4 h-4" /></button>
          <span className="w-11 h-11 shrink-0 rounded-full bg-gradient-to-br from-violet-500/60 to-indigo-500/60 flex items-center justify-center font-bold">{(th.user_name || th.user_email || '?').charAt(0).toUpperCase()}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <b className="truncate">{th.user_name || th.user_email || th.user_id}</b>
              <span title={th.lang === 'mg' ? 'Le client écrit en malagasy : répondez en malagasy' : 'Le client écrit en français'} className="text-[11px] px-1.5 py-0.5 rounded-md bg-white/10 font-bold">{FLAG[th.lang] || FLAG.fr} {th.lang === 'mg' ? 'Malagasy' : 'Français'}</span>
              <span className={`inline-flex items-center gap-1 text-[11px] ${data.client_online ? 'text-emerald-300' : 'text-gray-500'}`}><span className={`w-1.5 h-1.5 rounded-full ${data.client_online ? 'bg-emerald-400 animate-pulse' : 'bg-gray-500'}`} /> {data.client_online ? 'dans le chat' : 'hors ligne'}</span>
              {th.status === 'closed' && <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/10 text-gray-300">Fermée</span>}
              {th.status === 'reopen' && <span className="text-[11px] px-2 py-0.5 rounded-full bg-orange-500/25 border border-orange-400/50 text-orange-200 font-semibold animate-pulse">🔔 Demande de réouverture</span>}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400 mt-0.5">
              {th.user_email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {th.user_email}</span>}
              {th.user_phone && <a href={`tel:${th.user_phone.replace(/\s/g, '')}`} className="flex items-center gap-1 text-amber-200 hover:text-amber-100 font-semibold"><Phone className="w-3 h-3" /> {th.user_phone}</a>}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {offers.slots.map((s, i) => (
                <span key={i} className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${STAT[s.status] || STAT.expired}`}>
                  {s.channel === 'whatsapp' ? <MessageCircle className="w-3 h-3" /> : <Facebook className="w-3 h-3" />}
                  {s.kind === 'trial' ? <Gift className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />} {KIND[s.kind] || s.kind}
                  {s.status === 'active' ? ` · ${s.days_left} j` : s.status === 'expired' ? ' · terminé' : ' · suspendu'}
                </span>
              ))}
              {offers.pages.map(p => (
                <span key={p.client_id} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-white/15 bg-white/5 text-gray-200">
                  <Building2 className="w-3 h-3" /> {p.name || 'Page'} <span className={`w-1.5 h-1.5 rounded-full ${p.bot_active ? 'bg-emerald-400' : 'bg-gray-500'}`} title={p.bot_active ? 'Agent actif' : 'Agent en pause'} />
                </span>
              ))}
              {!offers.slots.length && !offers.pages.length && <span className="text-[11px] text-gray-500">Aucune offre ni Page connectée</span>}
            </div>
          </div>
          <button onClick={() => setStatus(th.status !== 'open' ? 'open' : 'closed')} title={th.status !== 'open' ? 'Rouvrir' : 'Fermer la conversation (le client ne pourra plus écrire, seulement demander à rediscuter)'}
            className={`p-2 rounded-xl shrink-0 ${th.status === 'reopen' ? 'bg-orange-500 hover:bg-orange-400' : 'bg-white/10 hover:bg-white/20'}`}>{th.status !== 'open' ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}</button>
        </div>
      </div>

      {/* messages */}
      <div ref={boxRef} onScroll={() => { const e = boxRef.current; if (e) { const near = e.scrollHeight - e.scrollTop - e.clientHeight < 120; stick.current = near; setAtBottom(near); } }}
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4 flex flex-col gap-2 scroll-smooth" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,.25) transparent' }}>
        {msgs.map(m => {
          const day = dayLabel(m.created_at); const show = day !== lastDay; lastDay = day;
          const mine = m.sender === 'admin';
          const seen = mine && data.user_seen_at && data.user_seen_at >= m.created_at;
          return (
            <React.Fragment key={m.id}>
              {show && <div className="self-center px-3 py-1 rounded-full bg-white/10 text-[11px] text-gray-300 capitalize">{day}</div>}
              <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap break-words shadow-lg ${mine ? 'self-end bg-gradient-to-br from-violet-600 to-indigo-600 rounded-br-md' : 'self-start bg-white/10 border border-white/12 rounded-bl-md'}`}>
                {mine && <div className="text-[11px] font-bold text-indigo-100/80 mb-0.5">Support TeckiA{m.agent ? <span className="font-normal opacity-70"> · envoyé par {m.agent}</span> : null}</div>}
                {m.attachment && <AttachmentView m={m} onImage={(u, n) => setLightbox({ url: u, name: n })} />}
                {m.body}
                <div className="mt-1 flex items-center justify-end gap-1 text-[10.5px] text-white/55">{hhmm(m.created_at)}{mine && <CheckCheck className={`w-3.5 h-3.5 ${seen ? 'text-sky-300' : ''}`} title={seen ? 'Lu par le client' : 'Envoyé'} />}</div>
              </div>
            </React.Fragment>
          );
        })}
        <div ref={endRef} />
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-[80] bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <button className="absolute top-4 right-4 p-2 rounded-full bg-white/15 hover:bg-white/25" onClick={() => setLightbox(null)}><X className="w-5 h-5" /></button>
          <img src={lightbox.url} alt={lightbox.name} className="max-h-full max-w-full rounded-xl" onClick={e => e.stopPropagation()} />
        </div>
      )}

      {!atBottom && (
        <button onClick={() => { stick.current = true; endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); setAtBottom(true); }}
          className="absolute right-5 bottom-28 z-10 w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 shadow-xl shadow-indigo-900/60 flex items-center justify-center text-white hover:scale-110 transition" title="Aller au dernier message">
          <ChevronDown className="w-5 h-5" />
        </button>
      )}

      {th.status !== 'open' && (
        <div className={`flex-shrink-0 px-4 py-2.5 border-t flex items-center gap-3 text-sm ${th.status === 'reopen' ? 'bg-orange-500/15 border-orange-400/40 text-orange-100' : 'bg-white/5 border-white/10 text-gray-300'}`}>
          <Lock className="w-4 h-4 shrink-0" />
          <span className="flex-1">{th.status === 'reopen' ? 'Le client demande à rediscuter avec le support.' : 'Conversation fermée : le client ne peut plus écrire.'} Répondre ici la rouvre aussi.</span>
          <button onClick={() => setStatus('open')} className="shrink-0 px-3 py-1.5 rounded-lg bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold text-xs flex items-center gap-1.5"><Unlock className="w-3.5 h-3.5" /> Rouvrir</button>
        </div>
      )}

      {/* saisie */}
      <div className="flex-shrink-0 border-t border-white/10 bg-black/30 p-3">
        {canned && (
          <div className="mb-2 grid gap-1.5 max-h-48 overflow-auto">
            {(th.lang === 'mg' ? CANNED_MG : CANNED).map(c => <button key={c.label} onClick={() => { setText(c.text); setCanned(false); }} className="text-left rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-2 text-xs"><b className="text-violet-200">{c.label}</b><span className="block text-gray-400 truncate">{c.text}</span></button>)}
          </div>
        )}
        {err && <p className="text-sm text-rose-300 mb-2">{err}</p>}
        <div className="flex items-end gap-2">
          <input ref={fileRef} type="file" className="hidden" onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; upload(f); }} />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} title="Joindre un fichier" className="p-3 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 shrink-0 disabled:opacity-50">{uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}</button>
          <button onClick={() => setCanned(v => !v)} title="Réponses rapides" className={`p-3 rounded-xl border shrink-0 ${canned ? 'bg-violet-500/30 border-violet-400/60' : 'bg-white/10 border-white/10 hover:bg-white/20'}`}><Zap className="w-4 h-4" /></button>
          <textarea value={text} onChange={e => setText(e.target.value)} rows={2} maxLength={2000} placeholder="Votre réponse… (le client est notifié dans l'app)"
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            className="flex-1 resize-none rounded-xl bg-white/[0.07] border border-white/15 focus:border-violet-400/70 px-3 py-2 text-sm outline-none" />
          <button onClick={send} disabled={!text.trim() || sending} className="p-3 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 disabled:opacity-40 shrink-0">{sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}</button>
        </div>
      </div>
    </div>
  );
}

export default function SupportAdmin() {
  const [threads, setThreads] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [qLive, setQLive] = useState('');
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(null);
  const [showNew, setShowNew] = useState(false);

  useEffect(() => { const t = setTimeout(() => setQ(qLive), 300); return () => clearTimeout(t); }, [qLive]);

  const load = useCallback(async () => {
    try {
      const r = await api(`/support/threads?${new URLSearchParams({ q, status, per_page: 100 })}`);
      if (r.success) { setThreads(r.threads); setKpis(r.kpis); }
    } finally { setLoading(false); }
  }, [q, status]);
  useEffect(() => { load(); const t = setInterval(() => { if (!document.hidden) load(); }, 8000); return () => clearInterval(t); }, [load]);

  const CHIPS = [{ k: '', l: 'Toutes' }, { k: 'unread', l: 'Non lues' }, { k: 'waiting', l: 'Sans réponse' }, { k: 'reopen', l: 'Demandes' }, { k: 'open', l: 'Ouvertes' }, { k: 'closed', l: 'Fermées' }];

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] lg:h-screen p-3 md:p-5 gap-3 box-border text-white">
      <div className="flex-shrink-0 space-y-3 w-full max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center"><Headphones className="w-5 h-5" /></span>
              Support clients
            </h1>
            <p className="text-xs text-blue-200 mt-0.5">Conversations directes avec les clients, dans l'application</p>
          </div>
          <button onClick={() => setShowNew(true)} className="px-3.5 py-2 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 text-sm font-semibold flex items-center gap-2"><MessageSquarePlus className="w-4 h-4" /> Écrire à un client</button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <Kpi icon={Unlock} tone="text-orange-300" label="Demandes" value={kpis ? (kpis.reopen || 0) : '…'} onClick={() => setStatus('reopen')} active={status === 'reopen'} />
          <Kpi icon={Inbox} tone="text-rose-300" label="Non lues" value={kpis ? kpis.unread : '…'} onClick={() => setStatus('unread')} active={status === 'unread'} />
          <Kpi icon={Clock} tone="text-amber-300" label="Sans réponse" value={kpis ? kpis.waiting_reply : '…'} onClick={() => setStatus('waiting')} active={status === 'waiting'} />
          <Kpi icon={MessagesSquare} tone="text-sky-300" label="Ouvertes" value={kpis ? kpis.open : '…'} onClick={() => setStatus('open')} active={status === 'open'} />
          <Kpi icon={Headphones} tone="text-violet-300" label="Conversations" value={kpis ? kpis.total : '…'} onClick={() => setStatus('')} active={status === ''} />
        </div>
      </div>

      <div className="flex-1 min-h-0 w-full max-w-7xl mx-auto flex rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        {/* liste */}
        <div className={`${sel ? 'hidden md:flex' : 'flex'} w-full md:w-[360px] md:border-r border-white/10 flex-col min-h-0 shrink-0`}>
          <div className="p-3 space-y-2 border-b border-white/10 flex-shrink-0">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={qLive} onChange={e => setQLive(e.target.value)} placeholder="Nom, email, téléphone, message…" className="w-full bg-black/30 border border-white/10 rounded-xl pl-9 pr-3 py-1.5 text-sm outline-none focus:border-violet-400" />
            </div>
            <div className="flex gap-1.5 overflow-x-auto pb-0.5">
              {CHIPS.map(c => <button key={c.k} onClick={() => setStatus(c.k)} className={`shrink-0 px-3 py-1 rounded-full text-xs border transition ${status === c.k ? 'bg-violet-500/30 border-violet-400/60' : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'}`}>{c.l}</button>)}
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,.25) transparent' }}>
            {loading ? <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
              : threads.length === 0 ? <p className="py-12 text-center text-sm text-gray-400 px-4">Aucune conversation pour l'instant.</p>
                : threads.map(t => (
                  <button key={t.id} onClick={() => setSel(t.id)}
                    className={`w-full text-left flex items-start gap-3 px-3.5 py-3 border-b border-white/5 transition ${sel === t.id ? 'bg-violet-500/15' : 'hover:bg-white/[0.05]'}`}>
                    <span className="relative w-10 h-10 shrink-0 rounded-full bg-gradient-to-br from-violet-500/50 to-indigo-500/50 flex items-center justify-center font-bold text-sm">
                      {(t.user_name || t.user_email || '?').charAt(0).toUpperCase()}
                      {t.unread > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-[10px] font-bold flex items-center justify-center">{t.unread}</span>}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2"><b className={`truncate text-sm ${t.unread ? 'text-white' : 'text-gray-200'}`}>{t.lang === 'mg' ? '🇲🇬 ' : ''}{t.user_name || t.user_email || t.user_id}</b><span className="text-[11px] text-gray-500 shrink-0">{ago(t.last_at)}</span></span>
                      <span className={`block text-xs truncate mt-0.5 ${t.unread ? 'text-white font-semibold' : 'text-gray-400'}`}>{t.last_sender === 'admin' ? 'Vous : ' : ''}{t.last_message || '—'}</span>
                      {(t.user_phone || t.status !== 'open') && <span className="flex items-center gap-2 text-[11px] text-amber-200/80 mt-0.5">{t.user_phone}{t.status === 'closed' && <span className="text-gray-500">· fermée</span>}{t.status === 'reopen' && <span className="text-orange-300 font-semibold">· 🔔 demande à rediscuter</span>}</span>}
                    </span>
                  </button>
                ))}
          </div>
        </div>

        {/* conversation */}
        <div className={`${sel ? 'flex' : 'hidden md:flex'} flex-1 min-w-0 flex-col min-h-0 h-full`}>
          {sel ? <Conversation tid={sel} onBack={() => setSel(null)} onChanged={load} />
            : <div className="flex-1 flex flex-col items-center justify-center text-center text-gray-400 px-6"><Headphones className="w-12 h-12 mb-3 text-violet-300/50" /><p className="font-semibold text-gray-300">Choisissez une conversation</p><p className="text-sm mt-1">Vous verrez ici l'offre du client, son téléphone et ses Pages.</p></div>}
        </div>
      </div>
      {showNew && <NewConversation onClose={() => setShowNew(false)} onStarted={(id) => { load(); setSel(id); }} />}
    </div>
  );
}
